import { PropsWithChildren, useLayoutEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from '@lp/core/auth';
import { buildSignInUrl, consumeRedirectTarget, isSafeRedirectPath, REDIRECT_TO_PARAM } from '@/utils/helpers';
import { LoadingFallback } from './LoadingFallback';

export const AuthGuard = ({ children }: PropsWithChildren) => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { isAuthLoading, isLoggedIn, isUserDataInitialized } = useAuthContext();

  const isSignInRoute = pathname.startsWith('/sign-in');

  // Sole post-login redirect: SSO returns to '/' (stash), hard reloads keep the query param.
  // Consume runs as soon as logged in so the stash is always cleared, even for no-perms users.
  useLayoutEffect(() => {
    if (isAuthLoading || !isLoggedIn) return;
    const stashed = consumeRedirectTarget();
    if (!isUserDataInitialized) return;

    const fromQuery = isSignInRoute ? new URLSearchParams(search).get(REDIRECT_TO_PARAM) : null;
    const target = stashed ?? (isSafeRedirectPath(fromQuery) ? fromQuery : null);

    if (target) {
      navigate(target, { replace: true });
    } else if (isSignInRoute) {
      navigate('/', { replace: true });
    }
  }, [isAuthLoading, isLoggedIn, isUserDataInitialized, isSignInRoute, search, navigate]);

  if (isAuthLoading) return <LoadingFallback />;

  if (!isLoggedIn) {
    if (isSignInRoute) return children;
    // Failed OAuth exchange lands here on '/?code=…&state=…' — don't capture the dead callback URL.
    const params = new URLSearchParams(search);
    const isOAuthCallback = pathname === '/' && params.has('code') && params.has('state');
    return <Navigate to={buildSignInUrl(pathname, isOAuthCallback ? '' : search)} replace />;
  }

  if (!isUserDataInitialized) {
    return pathname.startsWith('/no-access') ? children : <Navigate to="/no-access" replace />;
  }

  // Logged in on /sign-in: the effect above navigates away; hold the spinner meanwhile.
  return isSignInRoute ? <LoadingFallback /> : children;
};
